'use client'

import { confirm } from '@repo/ui/Confirm'
import { Drawer } from '@repo/ui/Drawer'
import { formatDate } from '@repo/utils/time'
import { LockIcon } from '@repo/ui/LockIcon'
import { OverflowIcon } from '@repo/ui/OverflowIcon'
import { PopupWithBackdrop } from '@repo/ui/PopupWithBackdrop'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useEffect, useRef } from 'react'
import { useMenuState, MenuItem } from '@szhsin/react-menu'
import { useNavigate } from 'react-router-dom'
import { useNotificationStore } from '@repo/store/useNotificationStore'
import { useTheme } from '@mui/material/styles'
import { v4 as uuidv4 } from 'uuid'
import { WarningIcon } from '@repo/ui/WarningIcon'
import MUIBox from '@mui/material/Box'
import MUIDivider from '@mui/material/Divider'
import MUIIconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

export const ThirdPartyNotificationDrawer = () => {
  const { userId } = useAuthenticationStore()

  const {
    allNotifications,
    fetchAllNotifications,
    lockedNotifications,
    lockNotification,
    notificationDrawerIsOpen,
    setIsNotificationWizard,
    setNotificationDrawerIsOpen,
    setNotificationId,
    unlockNotification,
    updateNotificationStatus
  } = useNotificationStore()

  const theme = useTheme()
  const navigate = useNavigate()

  const notifications = allNotifications?.rows ?? []

  useEffect(() => {
    fetchAllNotifications({
      notificationStatusIds: 'OPEN',
      startAtIndex: 0,
      pageSize: 25
    })
  }, [lockedNotifications, fetchAllNotifications])

  const getIsNotificationLockedToAnotherUser = (notification: any) => {
    return notification.lockedByUserId ? notification.lockedByUserId.toLowerCase() !== userId.toLowerCase() : false
  }

  const navigateToClientMappingWizard = async (notificationId: number) => {
    const allNotifications = await fetchAllNotifications({
      notificationStatusIds: 'OPEN',
      startAtIndex: 0,
      pageSize: 25
    })

    const notif = allNotifications?.rows.find((n: any) => n.notificationId === notificationId)

    const isLockedToAnotherUser = getIsNotificationLockedToAnotherUser(notif)

    if (!isLockedToAnotherUser) {
      setNotificationDrawerIsOpen(false)
      setNotificationId(notificationId)
      setIsNotificationWizard(true)
      lockNotification(notificationId)
      navigate(
        `?wizard=NewMatter-NewClient-NotificationApproval&stage=new-matter-notification-approval&step=introduction`
      )
    } else {
      const lockedByUserIdNotification = notif.lockedByUserId ?? ''

      const lockedByEmailAddressNotification = notif.lockedByEmailAddress ? `(${notif.lockedByEmailAddress})` : ''

      const proceed = await confirm(
        `This new matter notification is currently locked out to ${lockedByUserIdNotification} ${lockedByEmailAddressNotification}. Please note that if you choose to proceed you may create duplicate data.`
      )
      if (!proceed) return

      await unlockNotification(notificationId)
      await setNotificationDrawerIsOpen(false)
      await setNotificationId(notificationId)
      await setIsNotificationWizard(true)
      await lockNotification(notificationId)
      navigate(
        `?wizard=NewMatter-NewClient-NotificationApproval&stage=new-matter-notification-approval&step=introduction`
      )
    }
  }

  return (
    <div data-testid="ThirdPartyNotificationDrawer">
      <Drawer
        isOpen={notificationDrawerIsOpen}
        onClose={() => setNotificationDrawerIsOpen(false)}
        headingNode={
          <MUITypography variant="h5" sx={{ mb: 0 }}>
            New matters to approve
          </MUITypography>
        }
      >
        <MUIBox
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          {notifications.map((notification: any) => {
          const isLocked = getIsNotificationLockedToAnotherUser(notification)

          const content = (
            <MUIStack
              key={`${notification.notificationId}-${uuidv4()}`}
              display="flex"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              height="auto"
              width="100%"
              sx={{
                padding: '1rem 1rem 1rem 1.5rem',
                backgroundColor: isLocked ? theme.palette.grey[100] : 'white',
                cursor: isLocked ? 'pointer' : 'default'
              }}
            >
              {isLocked ? (
                <LockIcon sx={{ height: '20px', width: '20px' }} />
              ) : (
                <WarningIcon sx={{ height: '20px', width: '20px' }} />
              )}
              {isLocked ? (
                <MUITooltip
                  placement="bottom"
                  arrow
                  sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                  title={
                    <MUIStack
                      display="flex"
                      direction="column"
                      justifyContent="center"
                      alignItems="flex-start"
                      height="auto"
                      width="100%"
                      flexGrow="1"
                      sx={{ cursor: 'pointer' }}
                    >
                      <MUITypography
                        variant="body1"
                        sx={{
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '1rem',
                          pl: 4,
                          pr: 4,
                          pb: 0,
                          pt: 4,
                          cursor: 'pointer'
                        }}
                      >
                        LOCKED BY
                      </MUITypography>
                      <MUITypography
                        variant="body1"
                        sx={{
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '0.85rem',
                          pl: 4,
                          pr: 4,
                          pb: 1,
                          pt: 1,
                          cursor: 'pointer'
                        }}
                      >
                        User ID: {notification.lockedByUserId ?? ''}
                      </MUITypography>
                      <MUITypography
                        variant="body2"
                        sx={{
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '0.85rem',
                          pl: 4,
                          pr: 4,
                          pb: 4,
                          pt: 0,
                          cursor: 'pointer'
                        }}
                      >
                        Email: {notification.lockedByEmailAddress ?? ''}
                      </MUITypography>
                    </MUIStack>
                  }
                >
                  <MUIStack
                    display="flex"
                    direction="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    height="auto"
                    width="100%"
                    flexGrow="1"
                    sx={{ backgroundColor: 'transparent', cursor: 'pointer', opacity: isLocked ? 0.5 : 1 }}
                  >
                    <MUITypography
                      variant="body1"
                      sx={{ fontWeight: '600', fontSize: '1rem', mb: 1, ml: 4, cursor: 'pointer' }}
                    >
                      {notification.description ?? 'New matter to approve'}
                    </MUITypography>
                    <MUITypography
                      variant="body2"
                      sx={{ fontWeight: '400', fontSize: '0.85rem', mb: 0, ml: 4, cursor: 'pointer' }}
                    >
                      {formatDate(notification.openedDateTime, 'EEE d MMM yyyy, h:mm a')}
                    </MUITypography>
                  </MUIStack>
                </MUITooltip>
              ) : (
                <MUIStack
                  display="flex"
                  direction="column"
                  justifyContent="center"
                  alignItems="flex-start"
                  height="auto"
                  width="100%"
                  flexGrow="1"
                >
                  <MUITypography
                    variant="body1"
                    sx={{ fontWeight: '600', fontSize: '1rem', mb: 1, ml: 4, cursor: 'default' }}
                  >
                    {notification.description ?? 'New matter to approve'}
                  </MUITypography>
                  <MUITypography
                    variant="body2"
                    sx={{ fontWeight: '400', fontSize: '0.85rem', mb: 0, ml: 4, cursor: 'default' }}
                  >
                    {formatDate(notification.openedDateTime, 'EEE d MMM yyyy, h:mm a')}
                  </MUITypography>
                </MUIStack>
              )}
              <ThirdPartyNotificationDrawerPopupMenu
                navigateToClientMappingWizard={navigateToClientMappingWizard}
                notification={notification}
                updateNotificationStatus={updateNotificationStatus}
                fetchAllNotifications={fetchAllNotifications}
              />
            </MUIStack>
          )

          return (
            <div key={notification.notificationId}>
              {content}
              <MUIDivider
                orientation="horizontal"
                sx={{
                  borderColor: 'divider',
                  borderRadius: '4px',
                  height: '0px',
                  padding: 0,
                  width: '90%',
                  ml: 6
                }}
              />
            </div>
          )
        })}
        </MUIBox>
      </Drawer>
    </div>
  )
}

const ThirdPartyNotificationDrawerPopupMenu = ({
  navigateToClientMappingWizard,
  notification,
  updateNotificationStatus,
  fetchAllNotifications
}: ThirdPartyNotificationDrawerPopupMenuProps) => {
  const [menuState, toggleMenu] = useMenuState({ transition: true })
  const ref = useRef(null)

  const viewNotification = async (notification: any) => {
    await navigateToClientMappingWizard(notification.notificationId)
    toggleMenu(false)
  }

  const closeNotification = async (notification: any) => {
    const allNotifications = await fetchAllNotifications({
      notificationStatusIds: 'OPEN',
      startAtIndex: 0,
      pageSize: 25
    })

    const notif = allNotifications?.rows.find((n: any) => n.notificationId === notification.notificationId)

    const isLocked = !!notif && (notif.lockedByEmailAddress !== null || notif.lockedByUserId !== null)

    if (!isLocked) {
      const proceed = await confirm(`You are about to close this notification. Are you sure you want to proceed?`)
      if (!proceed) return

      await updateNotificationStatus({ notificationId: notification.notificationId, notificationStatusId: 'CLOSED' })
      await fetchAllNotifications({
        notificationStatusIds: 'OPEN',
        startAtIndex: 0,
        pageSize: 25
      })
    } else {
      const lockedByUserIdNotification = notif.lockedByUserId ?? ''

      const lockedByEmailAddressNotification = notif.lockedByEmailAddress ? `(${notif.lockedByEmailAddress})` : ''

      const proceed = await confirm(
        `This new matter notification is currently locked out to ${lockedByUserIdNotification} ${lockedByEmailAddressNotification}. Please note that if you choose to proceed you may lose data.`
      )
      if (!proceed) return

      await updateNotificationStatus({ notificationId: notification.notificationId, notificationStatusId: 'CLOSED' })
      await fetchAllNotifications({
        notificationStatusIds: 'OPEN',
        startAtIndex: 0,
        pageSize: 25
      })
    }
  }

  return (
    <MUIStack sx={{ zIndex: '9999' }}>
      <MUIIconButton
        onClick={() => toggleMenu(true)}
        ref={ref}
        sx={{ margin: 0, marginTop: -1, width: '36px', height: '36px', right: '6px' }}
      >
        <OverflowIcon />
      </MUIIconButton>

      <PopupWithBackdrop
        menuState={menuState}
        anchorRef={ref}
        toggleMenu={toggleMenu}
        align="end"
        className="ThirdPartyNotificationDrawerPopupMenu"
        arrow
      >
        <MenuItem onClick={() => viewNotification(notification)}>
          <MUITypography
            variant="body2"
            sx={{
              color: '#5073C3',
              cursor: 'pointer',
              fontWeight: '600',
              textTransform: 'uppercase',
              fontSize: '0.85rem'
            }}
          >
            View
          </MUITypography>
        </MenuItem>
        <MenuItem onClick={() => closeNotification(notification)}>
          <MUITypography
            variant="body2"
            sx={{
              color: '#5073C3',
              cursor: 'pointer',
              fontWeight: '600',
              textTransform: 'uppercase',
              fontSize: '0.85rem'
            }}
          >
            Close
          </MUITypography>
        </MenuItem>
      </PopupWithBackdrop>
    </MUIStack>
  )
}

type ThirdPartyNotificationDrawerPopupMenuProps = {
  navigateToClientMappingWizard: (notificationId: number) => void
  notification: any
  updateNotificationStatus: ({
    notificationId,
    notificationStatusId
  }: {
    notificationId: number
    notificationStatusId: string
  }) => void
  fetchAllNotifications: ({
    notificationStatusIds,
    startAtIndex,
    pageSize
  }: {
    notificationStatusIds: string
    startAtIndex: number
    pageSize: number
  }) => Promise<any>
}
